import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../Components/Layout/Layout';
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button";
import StyledSwitch from "../Components/Layout/StyledSwitch";
import { ChevronDown, CheckCircle2 } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import ConfirmationModal from '../Components/Includes/ConfirmationModal';
import CopyCode from '../Components/Includes/CopyCode';
import CompanyAccessRequests from '../Components/Profile/CompanyAccessRequests';

const Profile = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
    const [isDepartmentDropdownOpen, setIsDepartmentDropdownOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [success, setSuccess] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [profile, setProfile] = useState({
        userid: '',
        firstname: '',
        lastname: '',
        email: '',
        companyid: '',
        company: {
            code: '',
            name: '',
            address: '',
            zipcode: '',
            city: '',
            phonenumber: ''
        },
        role: '',
        department: '',
        newsupdates: false
    });

    const roles = [
        { id: 'owner', name: t('profile.roles.owner') },
        { id: 'chef', name: t('profile.roles.chef') },
        { id: 'manager', name: t('profile.roles.manager') },
        { id: 'employee', name: t('profile.roles.employee') },
    ];

    const departments = [
        { id: 'kitchen', name: t('profile.departments.kitchen') },
        { id: 'service', name: t('profile.departments.service') },
    ];

    useEffect(() => {
        const fetchUserDetails = async () => {
            try {
                setIsLoading(true);
                const response = await axiosInstance.get('/user/GetUser');
                setProfile(response.data);
            } catch (error) {
                console.error('Error fetching profile:', error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserDetails();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (['companyname', 'address', 'zipcode', 'city', 'phonenumber'].includes(name)) {
            setProfile(prev => ({
                ...prev,
                company: {
                    ...prev.company,
                    [name === 'companyname' ? 'name' : name]: value
                }
            }));
        } else {
            setProfile(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleDeleteAccount = async () => {
        try {
            await axiosInstance.delete('/user/DeleteUser');
            navigate('/login');
        } catch (error) {
            console.error('Error deleting account:', error);
            setError(error.message);
        }
    };

    const handleSave = async () => {
        try {
            setIsSaving(true);
            setError(null);

            const response = await axiosInstance.post('/user/UpdateUser', {
                userid: profile.userid,
                firstname: profile.firstname,
                lastname: profile.lastname,
                email: profile.email,
                password: profile.password,
                company: {
                    companyid: profile.company.companyid,
                    name: profile.company.name,
                    address: profile.company.address,
                    zipCode: profile.company.zipcode,
                    city: profile.company.city,
                    phoneNumber: profile.company.phonenumber
                },
                role: profile.role,
                department: profile.department,
                newsupdates: profile.newsupdates
            });

            if (response.status === 200) {
                setShowSuccess(true);
                setSuccess(t('messages.profileSaved'));

                setTimeout(() => {
                    setShowSuccess(false);
                }, 2000);
            }
        } catch (error) {
            console.error('Error saving profile:', error);
            setError(error.message);
        } finally {
            setIsSaving(false);
            window.scrollTo({ top: 0, behavior: 'smooth' }); 
        }
    };

    if (isLoading) return <Layout><div>{t('common.loading')}</div></Layout>;
    if (error) return <Layout><div>{t('common.error')}: {error}</div></Layout>;

    return (
        <Layout>
            <div className="relative max-w-[1100px] mx-auto">
                <h2 className="text-xl font-normal font-inter text-ps-blue-dark mb-8">
                    {t('profile.title')}
                </h2>

                {showSuccess && (
                    <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-lg flex items-center gap-2">
                        <CheckCircle2 className="h-5 w-5" />
                        <span className="font-inter">{success}</span>
                    </div>
                )}

                {error && (
                    <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
                        <span className="font-inter">{error}</span>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="md:col-span-2 space-y-6">
                        <div className="p-2 mb-4">
                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.firstName')}
                            </label>
                            <Input
                                type="text"
                                name="firstname"
                                value={profile.firstname}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.lastName')}
                            </label>
                            <Input
                                type="text"
                                name="lastname"
                                value={profile.lastname}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.email')}
                            </label>
                            <Input
                                type="email"
                                name="email"
                                value={profile.email}
                                onChange={handleChange}
                                className="w-full p-2 border rounded bg-white"
                            />
                        </div>
                        </div>
                         
                        {profile.accountowner && (
                            <div className="bg-blue-50 p-4 pl-2 pr-2 mt-8">
                                <div className="mb-8">
                                <CopyCode code={profile.company.code} />
                                </div>

                                <div className="">
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('profile.companyName')}
                                    </label>
                                    <Input
                                        type="text"
                                        name="companyname"
                                        value={profile.company?.name}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded bg-white"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('profile.address')}
                                    </label>
                                    <Input
                                        type="text"
                                        name="address"
                                        value={profile.company?.address}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded bg-white"
                                    />
                                </div>

                                <div className="grid grid-cols-4 gap-4">
                                    <div className="col-span-1">
                                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                            {t('profile.zipcode')}
                                        </label>
                                        <Input
                                            type="text"
                                            name="zipcode"
                                            value={profile.company?.zipcode}
                                            onChange={handleChange}
                                            className="w-full p-2 border rounded bg-white"
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                            {t('profile.city')}
                                        </label>
                                        <Input
                                            type="text"
                                            name="city"
                                            value={profile.company?.city}
                                            onChange={handleChange}
                                            className="w-full p-2 border rounded bg-white"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                        {t('profile.phoneNumber')}
                                    </label>
                                    <Input
                                        type="tel"
                                        name="phonenumber"
                                        value={profile.company?.phonenumber}
                                        onChange={handleChange}
                                        className="w-full p-2 border rounded bg-white"
                                    />
                                </div>
                                 
                                <CompanyAccessRequests />
                            </div>
                        )}

                        <div className="p-2 mb-4">
                        <div className="relative pt-4">
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.role')}
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between p-2 border rounded bg-white text-left"
                                    onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                                >
                                    <span className="font-inter">
                                        {roles.find(r => r.id === profile.role)?.name || t('auth.selectRole')}
                                    </span>
                                    <ChevronDown className="h-4 w-4" />
                                </button>
                                {isRoleDropdownOpen && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                                        {roles.map((role) => (
                                            <div
                                                key={role.id}
                                                className="p-2 hover:bg-gray-100 cursor-pointer font-inter"
                                                onClick={() => {
                                                    setProfile(prev => ({ ...prev, role: role.id }));
                                                    setIsRoleDropdownOpen(false);
                                                }}
                                            >
                                                {role.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="relative">
                            <label className="block text-sm font-inter text-ps-blue-dark mb-2">
                                {t('profile.department')}
                            </label>
                            <div className="relative">
                                <button
                                    type="button"
                                    className="w-full flex items-center justify-between p-2 border rounded bg-white text-left"
                                    onClick={() => setIsDepartmentDropdownOpen(!isDepartmentDropdownOpen)}
                                >
                                    <span className="font-inter">
                                        {departments.find(d => d.id === profile.department)?.name || t('auth.selectDepartment')}
                                    </span>
                                    <ChevronDown className="h-4 w-4" />
                                </button>
                                {isDepartmentDropdownOpen && (
                                    <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg">
                                        {departments.map((department) => (
                                            <div
                                                key={department.id}
                                                className="p-2 hover:bg-gray-100 cursor-pointer font-inter"
                                                onClick={() => {
                                                    setProfile(prev => ({ ...prev, department: department.id }));
                                                    setIsDepartmentDropdownOpen(false);
                                                }}
                                            >
                                                {department.name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        </div>

                        <div className="p-2 mb-4">
                        <div className="flex items-center space-x-2 py-4">
                                <StyledSwitch
                                id="newsupdates"
                                checked={profile.newsupdates}
                                    onCheckedChange={(checked) => setProfile(prev => ({ ...prev, newsupdates: checked }))}
                            />
                            <label
                                    htmlFor="newsupdates"
                                className="font-inter text-sm text-ps-blue-dark cursor-pointer"
                            >
                                {t('profile.newsUpdates')}
                            </label>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex justify-start">
                                <Button
                                    variant="destructive"
                                    onClick={() => setIsDeleteModalOpen(true)}
                                    className="flex gap-2 w-60 bg-red-700 text-white hover:opacity-80"
                                >
                                    {t('profile.deleteAccount')}
                                </Button>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    onClick={handleSave}
                                    disabled={isSaving}
                                    className="bg-ps-blue-dark hover:opacity-80 text-white w-60"
                                >
                                    {isSaving ? t('common.saving') : t('common.save')}
                                </Button>
                            </div>

                            <ConfirmationModal
                                isOpen={isDeleteModalOpen}
                                onClose={() => setIsDeleteModalOpen(false)}
                                onConfirm={handleDeleteAccount}
                                title={t('messages.confirmDeleteAccount')}
                                message={t('messages.deleteAccountWarning')}
                            />
                        </div>
                    </div>

                    <div className="md:col-span-1">
                        {/* Rechter kolom kan gebruikt worden voor eventuele extra informatie */}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Profile;